import React, { useState } from "react";
import { Control, Controller, FieldValues, Path, useController } from "react-hook-form";
import { ClassNames, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Tooltip } from "antd5";

import FileInput from "components/form_components/FileInput";
import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { FileUploadStatus } from "lib/hooks/api/bid/bid_document/useUploadBidDocuments";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { Flex, Text } from "../../styles/utility-components";

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T, unknown>;
  uploadFileStatus: FileUploadStatus[];
  setUploadFileStatus: (fileUploadStatus: FileUploadStatus[]) => void;
  isUploadingBid: boolean;
};
export default function UploadDocumentsForm<T extends FieldValues>({
  name,
  control,
  uploadFileStatus,
  setUploadFileStatus,
  isUploadingBid,
}: Props<T>) {
  const { colors } = useTheme();
  const { field } = useController({
    name, // The field name from UploadDocumentsForm
    control,
  });

  const files = field.value;

  const [fileErrors, setFileErrors] = useState<string[]>([]);

  const handleUpdateFiles = (newFiles: File[]) => {
    if (isUploadingBid) {
      return;
    }
    field.onChange(newFiles);
    setUploadFileStatus(
      newFiles.map((file) => ({
        file,
        isError: false,
        isUploaded: false,
        isProcessed: false,
      })),
    );
  };
  const handleDeleteFile = (index: number) => {
    const updatedFiles: File[] = [...files];
    const removedFile = updatedFiles[index];

    // Update the form field (files list)
    updatedFiles.splice(index, 1);
    field.onChange(updatedFiles);

    // Remove matching file from upload status by name
    const updatedStatus = uploadFileStatus.filter(
      (status) => status.file.name !== removedFile.name,
    );
    setUploadFileStatus(updatedStatus);
  };

  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <FileInput
            {...field}
            acceptedFileTypes=".csv, .doc, .docx, .pptx, .xlsx, .odt, .odp, .ods, .pdf, .zip"
            errorMessage="Only certain file types are accepted"
            onChange={(files) => handleUpdateFiles(files)}
            onUnsupportedFilesError={(errors) => {
              setFileErrors(errors);
            }}
            title="Click or drag tender documents here to upload"
            listFileNamesInline={false}
          />
        )}
      />
      {fileErrors.length > 0 && (
        <ErrorContainer>
          <Text>Invalid file types:</Text>
          {fileErrors.map((error, index) => (
            <Text key={index}>{error}</Text>
          ))}
        </ErrorContainer>
      )}
      <UploadedDocumentsText>
        <Text h3>Uploaded documents {files.length > 0 && `(${files.length})`}</Text>
        {files.length === 0 && <Text>Your uploaded documents will appear here</Text>}
      </UploadedDocumentsText>
      <FilesList>
        {files.map((file: File, index: number) => (
          <FileContent key={file.name + index}>
            <Tooltip title={uploadFileStatus[index]?.isError ? "Document upload error" : undefined}>
              <Flex alignItems="center">
                <ClassNames>
                  {({ css }) => (
                    <>
                      <UIcon
                        icon={uploadFileStatus[index]?.isError ? "alertCircle" : "fileCheck02"}
                        color={
                          uploadFileStatus[index]?.isError
                            ? colors.sysDestructiveDefault
                            : colors.sysTextDefault
                        }
                        className={css({
                          marginRight: 4,
                        })}
                        size={16}
                      />
                      <EllipsisTooltipText
                        fullText={file.name}
                        containerClassname={css({
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          "-webkit-box-orient": "vertical",
                          lineClamp: 1,
                          color: uploadFileStatus[index]?.isError
                            ? colors.sysDestructiveDefault
                            : undefined,
                        })}
                      />
                    </>
                  )}
                </ClassNames>
              </Flex>
            </Tooltip>
            {uploadFileStatus[index]?.isProcessed ? (
              <ClassNames>
                {({ css }) => (
                  <UIcon
                    icon="checkOutlined"
                    color={colors.sysSuccessDefault}
                    className={css({
                      marginRight: 8,
                    })}
                    size={16}
                  />
                )}
              </ClassNames>
            ) : (
              <>
                {isUploadingBid ? (
                  <Text description color={colors.sysTextSecondary}>
                    {!uploadFileStatus[index]?.isUploaded ? "Uploading..." : "Processing..."}
                  </Text>
                ) : (
                  <Tooltip title="Delete">
                    <Button
                      buttonType="tertiary"
                      icon="trash01"
                      iconProps={{
                        color: uploadFileStatus[index]?.isError
                          ? colors.sysDestructiveDefault
                          : colors.sysTextDefault,
                      }}
                      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        e.stopPropagation();
                        handleDeleteFile(index);
                      }}
                    />
                  </Tooltip>
                )}
              </>
            )}
          </FileContent>
        ))}
      </FilesList>
    </>
  );
}

const FilesList = styled(Flex)(({ theme }) => ({
  gap: 16,
  flexDirection: "column",
  maxHeight: 400,
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: 10,
    float: "right",
  },
  "&::-webkit-scrollbar-track": {
    boxShadow: "inset 0 0 0px $placeholder-grey",
    borderRadius: 10,
  },

  "&::-webkit-scrollbar-thumb": {
    background: theme.colors.sysTextPlaceholder,
    borderRadius: 10,
  },

  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.colors.sysTextPlaceholder,
  },
}));

const ErrorContainer = styled(Flex)(({ theme }) => ({
  color: theme.colors.sysDestructiveDefault,
  fontWeight: 500,
  lineHeight: 22,
  marginBottom: 8,
  gap: 8,
}));

const FileContent = styled(Flex)({
  gap: 24,
  width: "100%",
  alignItems: "center",
  justifyContent: "space-between",
});

const UploadedDocumentsText = styled(Flex)({
  gap: 8,
  flexDirection: "column",
  marginTop: 32,
});
